/**
 * Project: Gus's Auto Dealership
 * Author: Eihab Khudhair
 * Description: VisitorTracker.
 */

'use client';

import { useEffect } from 'react';

function safeUUID() {
  // Use globalThis.crypto to avoid TS inferring `crypto` as `never`
  const c = globalThis.crypto;

  // Prefer randomUUID when available
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }

  // Fallback: RFC4122 v4 using getRandomValues
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);

    // RFC4122 version + variant bits
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
      16,
      20
    )}-${hex.slice(20)}`;
  }

  // Last resort
  return `v_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getOrCreateVisitorId() {
  const key = 'gus_visitor_id';
  let id = localStorage.getItem(key);

  if (!id) {
    id = safeUUID();
    localStorage.setItem(key, id);
  }

  return id;
}

export default function VisitorTracker() {
  useEffect(() => {
    const visitorId = getOrCreateVisitorId();
    const base = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

    const ping = async () => {
      try {
        await fetch(`${base}/cars/track-visitor`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitorId }),
        });
      } catch {
        // ignore (tracking shouldn't break UI)
      }
    };

    ping();
    const interval = setInterval(ping, 15000);

    const onVis = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return null;
}
